<template>
  <V3AppConnectionError v-if="kind === 'chunk'" />

  <div v-else class="fixed inset-0 bg-slate-50 px-5 flex items-center justify-center" data-testid="v3-fatal-error">
    <div class="w-full max-w-md">
      <div class="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div class="p-8 text-center space-y-4">
          <div class="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
            <svg
              class="w-7 h-7 text-amber-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <h1 class="text-xl font-bold text-gray-900">
            {{ kind === 'notfound' ? 'ไม่พบหน้านี้' : 'เกิดข้อผิดพลาด' }}
          </h1>
          <p class="text-gray-500">
            {{ kind === 'notfound' ? 'ลิงก์อาจไม่ถูกต้องหรือถูกย้ายไปแล้ว' : 'กรุณาลองใหม่อีกครั้ง' }}
          </p>
          <p v-if="kind === 'fatal' && detail" class="text-sm text-gray-400 break-all">{{ detail }}</p>

          <div class="pt-2 space-y-2">
            <!-- Reloading a 404 can never help, so that branch only goes home. -->
            <button
              v-if="kind === 'fatal'"
              @click="reload"
              type="button"
              class="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              data-testid="v3-fatal-error-reload"
            >
              โหลดใหม่
            </button>
            <button
              @click="goHome"
              type="button"
              class="w-full py-3 px-4 rounded-lg font-medium transition-colors"
              :class="kind === 'notfound'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-white border text-gray-700'"
              data-testid="v3-fatal-error-home"
            >
              กลับหน้าแรก
            </button>
          </div>

          <p class="text-sm text-gray-400">หรือสแกน QR Code ใหม่ที่โต๊ะ</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// error.vue — Nuxt renders this INSTEAD of `app.vue` when an error is fatal,
// which is why the overlay in `app.vue` cannot cover this case on its own.
//
// Three outcomes, because they need three different offers:
//
//   'chunk'    a chunk failed to load on a direct visit or a refresh. Recoverable
//              — the connection screen offers a reload.
//   'notfound' an unknown path. Firebase rewrites everything to the SPA shell,
//              so a typo lands here rather than on a server 404. Reloading can
//              never fix it, so this branch only offers the way home.
//   'fatal'    anything else. Reload first, home second.
import { computed } from 'vue'
import type { NuxtError } from '#app'
import { isChunkError, useChunkError } from '~/composables/v3/useChunkError'

const props = defineProps<{ error: NuxtError }>()

const chunkErrorFlag = useChunkError()

const message = computed<string>(() =>
  `${props.error?.message ?? ''} ${props.error?.statusMessage ?? ''}`.toLowerCase(),
)

const kind = computed<'chunk' | 'notfound' | 'fatal'>(() => {
  if (chunkErrorFlag.value) return 'chunk'
  if (isChunkError(message.value)) return 'chunk'
  if (props.error?.statusCode === 404) return 'notfound'
  return 'fatal'
})

/** Nuxt's own message — useful in a bug report, harmless to show. */
const detail = computed<string>(() => props.error?.message ?? '')

function reload(): void {
  window.location.reload()
}

/** Full page load, not `navigateTo`: the router may be the thing that broke. */
function goHome(): void {
  window.location.href = '/v3'
}
</script>
